import { useRef, useState } from 'react'
import gql from 'graphql-tag'
import { v4 as uuid } from 'uuid'
import { LlmChatMessageSenderType } from '@/__gen/types'
import { apolloClient } from '@/state/apollo-client'
import { useFormContext } from '@/state/main-form/reducer'
import { PollLlmChatQuery, usePollLlmChatLazyQuery, useSendMessageMutation } from './__gen/useLlm'

gql`
	mutation sendMessage(
		$id: ID!
		$content: String!
		$form: ID!
		$chat: ID!
		$recordId: ID!
	) {
		llmChatMessage_Update(
			input: {
				id: $id
				content: $content
				chat: $chat
				record: $recordId
				form: $form
			}
		) {
			object {
				id
				chat {
					id
				}
			}
			errors {
				field
				messages
			}
		}
	}
`

const GET_MESSAGES_QUERY = gql`
	query PollLlmChat($chatId: ID!) {
		llmChat(id: $chatId) {
			id
			created
			messages(orderBy: [{ field: created, direction: ASC }]) {
				id
				content
				created
				sender
				responseReceivedAt
			}
		}
	}
`

export const useLlm = () => {
	const [sendMessage] = useSendMessageMutation()
	const [get, { data }] = usePollLlmChatLazyQuery({})
	const formContext = useFormContext()
	const chatIdRef = useRef<string>(uuid())
	const [loading, setLoading] = useState(false)
	const [streamingContent, setStreamingContent] = useState<string | null>(null)

	console.log(data)

	const send = async ({ content }: { content: string }) => {
		const id = uuid()
		const variables = { chatId: chatIdRef.current }
		const existing = apolloClient.readQuery<PollLlmChatQuery>({ query: GET_MESSAGES_QUERY, variables })
		apolloClient.writeQuery<PollLlmChatQuery>({
			query: GET_MESSAGES_QUERY,
			variables,
			data: {
				__typename: 'PharosQuery',
				llmChat: {
					__typename: 'LlmChatType',
					id: chatIdRef.current,
					created: existing?.llmChat?.created ?? new Date().toISOString(),
					messages: [
						...(existing?.llmChat?.messages ?? []),
						{
							__typename: 'LLmChatMessageType',
							id,
							content,
							created: new Date().toISOString(),
							responseReceivedAt: null,
							sender: LlmChatMessageSenderType.User,
						},
					],
				},
			},
		})
		get({ variables, fetchPolicy: 'cache-only' })
		setLoading(true)
		const resp = await sendMessage({
			variables: {
				id,
				chat: chatIdRef.current,
				content,
				form: formContext.form.id,
				recordId: formContext.record.id,
			},
		})

		if (resp.errors || !resp.data?.llmChatMessage_Update?.object) {
			setLoading(false)
			return
		}

		const messageId = resp.data.llmChatMessage_Update.object.id
		await streamResponse(messageId)
	}

	const streamResponse = (messageId: string) =>
		new Promise<void>((resolve) => {
			const eventSource = new EventSource(`/api/v1/llm/messages/${messageId}/stream`)
			let accumulated = ''

			eventSource.onmessage = (event) => {
				console.log('event', event)
				if (event.data === '[DONE]') {
					eventSource.close()
					setStreamingContent(null)
					get({ variables: { chatId: chatIdRef.current }, fetchPolicy: 'network-only' })
					resolve()
					return
				}
				try {
					const parsed = JSON.parse(event.data) as { text: string }
					accumulated += parsed.text
					setStreamingContent(accumulated)
					setLoading(false)
				} catch {
					// ignore malformed chunks
				}
			}

			eventSource.onerror = () => {
				eventSource.close()
				setStreamingContent(null)
				setLoading(false)
				resolve()
			}
		})

	const baseMessages = data?.llmChat?.messages ?? null
	const messages =
		streamingContent !== null && baseMessages
			? [
					...baseMessages,
					{
						__typename: 'LLmChatMessageType' as const,
						id: 'streaming',
						content: streamingContent,
						created: new Date().toISOString(),
						sender: LlmChatMessageSenderType.Agent,
						responseReceivedAt: null,
					},
				]
			: baseMessages

	return {
		send,
		loading,
		messages,
	}
}
